<template>
  <component
    :is="iconComponent"
    :size="sizeMap[size]"
    :stroke-width="1.5"
    :class="$style.icon"
    :aria-label="name"
  />
</template>

<script setup lang="ts">
import {
  Activity,
  Bike,
  BookOpen,
  Briefcase,
  Building2,
  Bus,
  Car,
  Coffee,
  DollarSign,
  Dumbbell,
  GraduationCap,
  Heart,
  HelpCircle,
  Home,
  Info,
  Laptop,
  Leaf,
  Library,
  Map,
  Moon,
  PersonStanding,
  PlusCircle,
  School,
  Shield,
  ShoppingBag,
  ShoppingCart,
  Stethoscope,
  TrafficCone,
  Train,
  TreePine,
  Trees,
  User,
  Users,
  Utensils,
  UtensilsCrossed,
  Wifi,
} from 'lucide-vue-next';
import { computed, type Component } from 'vue';

const props = withDefaults(
  defineProps<{
    name: string;
    size?: 'small' | 'medium' | 'large';
  }>(),
  {
    size: 'medium',
  },
);

const sizeMap = {
  small: 16,
  medium: 20,
  large: 24,
};

const iconMap: Record<string, Component> = {
  // ============================================================================
  // LIFESTYLE DNA ICONS (7 types from backend spec)
  // ============================================================================
  community: Users,
  leaf: Leaf,
  traffic: TrafficCone,
  public_transport: Train,
  shop: ShoppingCart,
  work: Briefcase,
  study: BookOpen,

  // ============================================================================
  // KEY PLACE ICONS (8 types from backend spec)
  // ============================================================================
  school: School,
  park: TreePine,
  doctor: Stethoscope,
  food: UtensilsCrossed,

  // ============================================================================
  // ADDITIONAL MAPPINGS & ALIASES
  // ============================================================================
  // Work/Office
  briefcase: Briefcase,
  coworking: Laptop,
  laptop: Laptop,

  // Education
  'graduation-cap': GraduationCap,
  university: GraduationCap,
  kindergarten: School,
  library: Library,
  'book-open': BookOpen,
  book: BookOpen,

  // Nature/Parks
  tree: Trees,
  trees: Trees,
  playground: Trees,
  'tree-pine': TreePine,

  // Community/People
  users: Users,
  user: User,
  'person-standing': PersonStanding,
  walk: PersonStanding,

  // Transport
  train: Train,
  station: Train,
  bus: Bus,
  bike: Bike,
  car: Car,

  // Shopping/Food
  'shopping-cart': ShoppingCart,
  supermarket: ShoppingCart,
  'shopping-bag': ShoppingBag,
  cafe: Coffee,
  coffee: Coffee,
  restaurant: Utensils,
  utensils: Utensils,
  fast_food: Utensils,

  // Healthcare
  stethoscope: Stethoscope,
  pharmacy: Stethoscope,

  // Sports/Activity
  activity: Activity,
  dumbbell: Dumbbell,
  gym: Dumbbell,
  sports_centre: Dumbbell,

  // Buildings
  building: Building2,
  'building-2': Building2,
  home: Home,

  // Other
  shield: Shield,
  'dollar-sign': DollarSign,
  dollar: DollarSign,
  heart: Heart,
  'plus-circle': PlusCircle,
  wifi: Wifi,
  'map-pin': Map,
  info: Info,
  moon: Moon,
};

const iconComponent = computed(() => {
  const normalizedName = props.name.toLowerCase();
  const component = iconMap[normalizedName];

  return component || HelpCircle;
});
</script>

<style module lang="scss">
.icon {
  color: #1f2937;
  flex-shrink: 0;
}
</style>
